import React from 'react';
import { Tooltip, Popconfirm } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faLaptop,
  faLocationDot,
} from '@fortawesome/free-solid-svg-icons';
import { DeviceCardProps, AssignmentStatus } from './types';
import { LABELS, DEVICE_TYPE_BADGE_COLORS, MESSAGES, ASSIGNMENT_COLORS } from './constants';
import {
  DeviceCardWrapper,
  DeviceCardContent,
  DeviceCardHeader,
  DeviceInfo,
  DeviceName,
  DeviceIdentifier,
  DeviceTypeBadge,
  DeviceCardDivider,
  DeviceMetaRow,
  DeviceMetaIcon,
  DeviceMetaText,
  AssignmentTag,
  DeviceCardActions,
  ActionButton,
  DangerActionButton,
} from './styles';

const getDeviceTypeName = (device: DeviceCardProps['device']): string => {
  return typeof device.type === 'object' ? device.type?.name || 'Unknown' : 'Unknown';
};

const getDeviceTypeColor = (typeName: string): string => {
  const key = typeName.toLowerCase();
  return DEVICE_TYPE_BADGE_COLORS[key] || DEVICE_TYPE_BADGE_COLORS.default;
};

const getAssignmentStatus = (device: DeviceCardProps['device']): AssignmentStatus => {
  const hasStore = typeof device.store === 'object' && device.store?.id;
  if (hasStore) {
    const storeName = typeof device.store === 'object' ? device.store.name : '';
    return {
      status: 'assigned',
      color: ASSIGNMENT_COLORS.ASSIGNED,
      text: `${LABELS.ASSIGNED} to ${storeName}`,
    };
  }
  return {
    status: 'unassigned',
    color: ASSIGNMENT_COLORS.UNASSIGNED,
    text: LABELS.UNASSIGNED,
  };
};

const DeviceCard: React.FC<DeviceCardProps> = ({
  device,
  isDark,
  onEdit,
  onDelete,
}) => {
  const typeName = getDeviceTypeName(device);
  const typeColor = getDeviceTypeColor(typeName);
  const assignment = getAssignmentStatus(device);

  return (
    <DeviceCardWrapper $isDark={isDark}>
      <DeviceCardContent>
        <DeviceCardHeader>
          <DeviceInfo>
            <DeviceName $isDark={isDark}>{device.name}</DeviceName>
            <DeviceIdentifier $isDark={isDark}>{device.identifier}</DeviceIdentifier>
          </DeviceInfo>
          <DeviceTypeBadge color={typeColor}>{typeName}</DeviceTypeBadge>
        </DeviceCardHeader>

        <DeviceCardDivider $isDark={isDark} />

        <DeviceMetaRow>
          <DeviceMetaIcon $isDark={isDark}>
            <FontAwesomeIcon icon={faLocationDot} />
          </DeviceMetaIcon>
          <AssignmentTag
            $isAssigned={assignment.status === 'assigned'}
            color={assignment.status === 'assigned' ? 'success' : 'error'}
          >
            {assignment.text}
          </AssignmentTag>
        </DeviceMetaRow>

        <DeviceMetaRow>
          <DeviceMetaIcon $isDark={isDark}>
            <FontAwesomeIcon icon={faLaptop} />
          </DeviceMetaIcon>
          <DeviceMetaText $isDark={isDark}>{typeName}</DeviceMetaText>
        </DeviceMetaRow>

        <DeviceCardDivider $isDark={isDark} />

        <DeviceCardActions>
          <Tooltip title={LABELS.EDIT_DEVICE}>
            <ActionButton
              $isDark={isDark}
              type="text"
              icon={<FontAwesomeIcon icon={faEdit} />}
              onClick={() => onEdit(device)}
            />
          </Tooltip>
          <Popconfirm
            title={MESSAGES.CONFIRM_DELETE}
            onConfirm={() => onDelete(device.id)}
            okText={LABELS.YES}
            cancelText={LABELS.NO}
          >
            <Tooltip title={LABELS.DELETE_DEVICE}>
              <DangerActionButton
                type="text"
                icon={<FontAwesomeIcon icon={faTrash} />}
              />
            </Tooltip>
          </Popconfirm>
        </DeviceCardActions>
      </DeviceCardContent>
    </DeviceCardWrapper>
  );
};

export default DeviceCard;
